/**
 * Who is signed in, and the token every request carries.
 *
 * The token lives in localStorage so a refresh does not sign you out. That is a
 * deliberate trade: it is readable by any script on this origin, which is
 * acceptable here because the alternative (an httpOnly cookie) needs CSRF
 * protection and a same-site deployment, and this app is a local/SPA tool. The
 * tokens are short-lived and server-side revocable via `token_version`.
 *
 * "Keep me signed in" only asks the server for a longer expiry — it does not
 * change where the token is kept.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { configureApiAuth } from '@/core/api/base.api.client'
import { queryClient } from '@/lib/query.client'
import { sessionApi, type SessionUser } from './session.api'

const TOKEN_KEY = 'context-graph.token'
const USER_KEY = 'context-graph.user'

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function persist(token: string | null, user: SessionUser | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token)
    else window.localStorage.removeItem(TOKEN_KEY)
    if (user) window.localStorage.setItem(USER_KEY, JSON.stringify(user))
    else window.localStorage.removeItem(USER_KEY)
  } catch {
    // Storage can be blocked; the session then lasts only for this page.
  }
}

interface AuthContextValue {
  user: SessionUser | null
  token: string | null
  /** False on a fresh server with no accounts — the app is open in that case. */
  loginRequired: boolean
  /** True until we know whether the stored token is still valid. */
  initialising: boolean
  signIn: (username: string, password: string, remember: boolean) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readToken())
  const [user, setUser] = useState<SessionUser | null>(() => read<SessionUser>(USER_KEY))
  const [loginRequired, setLoginRequired] = useState(true)
  const [initialising, setInitialising] = useState(true)

  // Registered before anything fetches, so the very first request carries the
  // stored token rather than racing the provider's first render.
  useEffect(() => {
    configureApiAuth(
      async () => readToken(),
      () => {
        // The server rejected our token: clear it so the guard shows sign-in
        // rather than leaving the UI stuck retrying.
        persist(null, null)
        setToken(null)
        setUser(null)
      },
    )
  }, [])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const mode = await sessionApi.mode()
        if (cancelled) return
        setLoginRequired(mode.login_required)

        if (!mode.login_required) {
          // No accounts exist; the API allows anonymous access.
          setInitialising(false)
          return
        }

        if (readToken()) {
          // Confirm the stored token still works before trusting the cached user.
          try {
            const me = await sessionApi.me()
            if (cancelled) return
            setUser({
              id: me.user_id,
              username: me.username ?? 'user',
              display_name: me.display_name,
              email: me.email,
              is_admin: me.is_admin,
              workspace_id: me.workspace_id,
            })
          } catch {
            if (cancelled) return
            persist(null, null)
            setToken(null)
            setUser(null)
          }
        }
      } catch {
        // The API is unreachable. Assume sign-in is needed rather than letting
        // someone through on a network error.
        if (!cancelled) setLoginRequired(true)
      } finally {
        if (!cancelled) setInitialising(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(
    async (username: string, password: string, remember: boolean) => {
      const result = await sessionApi.login(username, password, remember)
      persist(result.access_token, result.user)
      setToken(result.access_token)
      setUser(result.user)
      // Nothing cached under the previous identity should survive.
      queryClient.clear()
    },
    [],
  )

  const signOut = useCallback(async () => {
    try {
      await sessionApi.logout()
    } catch {
      // Signing out locally must succeed even if the call does not.
    }
    persist(null, null)
    setToken(null)
    setUser(null)
    queryClient.clear()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loginRequired, initialising, signIn, signOut }),
    [user, token, loginRequired, initialising, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
